import type { ReactNode } from 'react'
import LineDivider from 'ui/atoms/Line/LineDivider'
import { confirmDayAheadBidTableLeave } from '../../dayAheadBidTableLeave.helper'
import { DayAheadBidInputModeSelector } from './DayAheadBidInputModeSelector'
import type { DayAheadBidTableResetController } from './DayAheadBidTable.types'
import { DayAheadBidTablePanel } from './DayAheadBidTablePanel'
import { useDayAheadBidTableMode } from './useDayAheadBidTableMode'

type DayAheadBidInputSurfaceProps = {
  partitionId: number | undefined
  startTime: ISODateTime
  endTime: ISODateTime
  requireTagForBlockBids: boolean
  tableContextKey: string
  tableResetController: DayAheadBidTableResetController
  children: ReactNode
}

export function DayAheadBidInputSurface({
  partitionId,
  startTime,
  endTime,
  requireTagForBlockBids,
  tableContextKey,
  tableResetController,
  children,
}: DayAheadBidInputSurfaceProps): ReactNode {
  const { isModeSwitchVisible, activeMode, setMode } = useDayAheadBidTableMode({
    startTime,
    endTime,
    requireTagForBlockBids,
  })

  function handleModeChange(mode: 'table' | 'modal'): void {
    if (mode === activeMode || confirmDayAheadBidTableLeave('mode')) {
      setMode(mode)
    }
  }

  if (!isModeSwitchVisible) {
    return (
      <>
        <LineDivider />
        {children}
      </>
    )
  }

  return (
    <>
      <div className="pt-2">
        <LineDivider />
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4">
        <DayAheadBidInputModeSelector activeMode={activeMode} onModeChange={handleModeChange} />
      </div>

      <div role="tabpanel" id={`tabpanel-${activeMode}`} aria-labelledby={`tab-${activeMode}`}>
        {activeMode === 'table' ? (
          <DayAheadBidTablePanel
            partitionId={partitionId}
            tableContextKey={tableContextKey}
            tableResetController={tableResetController}
          />
        ) : (
          children
        )}
      </div>
    </>
  )
}
